import {
  MomentThumbnail,
  MomentUnavailableThumbnail,
  NoteThumbnail,
  NoteUnavailableThumbnail,
  SwapThumbnail,
  SwapUnavailableThumbnail,
} from "@/components/shared/entity-thumbnails";
import type { ChatRenderContext } from "@/components/shared/chat-message-types";
import { useEntityShared } from "@/lib/hooks/conversations/use-entity-shared";

interface EntitySharedMessageRendererProps {
  message: {
    text: string;
    customType: string;
    data?: string;
  };
  context?: ChatRenderContext;
}

export function EntitySharedMessageRenderer({
  message,
  context,
}: EntitySharedMessageRendererProps) {
  const { entityData, isUnavailable, ownerName, openInHub } = useEntityShared(
    message,
    { context },
  );
  const isAdminSurface = context?.surface === "admin";
  const fallbackText = message.text || "Shared update";

  if (!entityData?.entity_id || !entityData?.entity_type) {
    return <EntitySharedFallback text={fallbackText} />;
  }

  const getMessage = () => {
    if (isAdminSurface) {
      return fallbackText;
    }

    if (entityData.entity_type === "swap") {
      switch (entityData.swap_action) {
        case "completed":
          return "Swap completed!";
        case "started":
          return `${ownerName} started a swap`;
        default:
          return `${ownerName} added a photo to the swap`;
      }
    }
    return `${ownerName} added a ${entityData.entity_type}`;
  };

  const renderThumbnail = () => {
    switch (entityData.entity_type) {
      case "moment":
        return (
          <MomentThumbnail previewUrl={entityData.preview_url} size="sm" />
        );
      case "swap":
        return (
          <SwapThumbnail
            previewUrl={entityData.preview_url}
            secondPreviewUrl={entityData.second_preview_url}
            size="sm"
          />
        );
      default:
        return <NoteThumbnail size="sm" />;
    }
  };

  if (isUnavailable) {
    const renderUnavailableThumbnail = () => {
      switch (entityData.entity_type) {
        case "moment":
          return <MomentUnavailableThumbnail size="sm" />;
        case "swap":
          return <SwapUnavailableThumbnail size="sm" />;
        default:
          return <NoteUnavailableThumbnail size="sm" />;
      }
    };

    return (
      <div className="flex items-center justify-center py-3">
        <div className="flex items-center gap-3">
          {renderUnavailableThumbnail()}
          <span className="text-sm text-zinc-400">
            This {entityData.entity_type} is no longer available
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center py-3">
      <button
        onClick={openInHub}
        className="flex cursor-pointer items-center gap-3 transition-opacity hover:opacity-80"
      >
        {renderThumbnail()}
        <span className="text-sm text-zinc-600">{getMessage()}</span>
      </button>
    </div>
  );
}

function EntitySharedFallback({ text }: { text: string }) {
  return (
    <div className="flex items-center justify-center py-3">
      <span className="text-sm text-secondary">{text}</span>
    </div>
  );
}
